// ============================================================================
// KAN-58：Gastric varices（胃靜脈曲張）Form 欄位「各級別數量」編碼工具（前端鏡像）
//
// ⚠️ 這份是 `server/public/tool/form-quantity.js` 的前端鏡像（前端跨 build 邊界無法
//     require server 檔）。編碼格式 / option id 對照的真相源在 server 那份；兩份必須
//     一起改。
//
// 編碼格式（存 records.form）：`F1:2;F2:1`（level:qty，分號分隔，只存有填、qty>=1）。
// 向下相容：舊紀錄 records.form 是 option id（258/259/260），解析時對映回 F1/F2/F3。
// ============================================================================

// F 級別（顯示與編碼順序固定 F1→F2→F3）
export const FORM_LEVELS = ['F1', 'F2', 'F3']

// 舊單選 option id → 級別（來源 server/sql/seeds/options.js id 258/259/260，fieldID 14）
export const FORM_OPTION_ID_TO_LEVEL = {
  258: 'F1',
  259: 'F2',
  260: 'F3',
}

// 新編碼格式：F1:2;F2:1
const ENCODED_FORM_QUANTITY_RE = /^\s*F[123]\s*:\s*\d+\s*(?:;\s*F[123]\s*:\s*\d+\s*)*$/i

/**
 * 是否為 KAN-58 新編碼（與舊純數字 option id 區分）
 * @param {*} value
 * @returns {boolean}
 */
export function isEncodedFormQuantity (value) {
  return typeof value === 'string' && ENCODED_FORM_QUANTITY_RE.test(value.trim())
}

/**
 * 把 records.form 原始值解析成三級別數量物件 { F1, F2, F3 }（空字串代表未填）。
 * 支援三種輸入：新編碼("F1:2;F2:1")、舊 option id("260")、空值。
 * @param {*} value
 * @returns {{ F1: (number|''), F2: (number|''), F3: (number|'') }}
 */
export function parseToQuantities (value) {
  const result = { F1: '', F2: '', F3: '' }
  if (value === null || value === undefined || value === '') return result

  const raw = String(value).trim()

  if (isEncodedFormQuantity(raw)) {
    raw.split(';').forEach((pair) => {
      const [rawLevel, rawQty] = pair.split(':')
      const level = String(rawLevel).trim().toUpperCase()
      const qty = parseInt(String(rawQty).trim(), 10)
      if (FORM_LEVELS.includes(level) && Number.isInteger(qty) && qty >= 1) {
        result[level] = qty
      }
    })
    return result
  }

  // 舊 option id（單選）→ 對映級別，數量以 1 呈現（讓醫師看到原本被選的級別，
  // 之後儲存會轉為新編碼）。無法對映的舊值不處理（維持全空、不誤寫）。
  const level = FORM_OPTION_ID_TO_LEVEL[raw]
  if (level) result[level] = 1
  return result
}

/**
 * 把三級別數量物件組成新編碼 "F1:2;F2:1"（只含 qty>=1；全空回空字串）。
 * @param {{ F1:*, F2:*, F3:* }} quantities
 * @returns {string}
 */
export function buildEncoded (quantities) {
  return FORM_LEVELS.filter((lv) => {
    const qty = parseInt(quantities[lv], 10)
    return Number.isInteger(qty) && qty >= 1
  })
    .map((lv) => `${lv}:${parseInt(quantities[lv], 10)}`)
    .join(';')
}

/**
 * 把新編碼顯示成人類可讀字串 `F1 x2, F2 x1`（只列有填、順序 F1→F2→F3）。
 * 與 server `formatFormQuantityForReport` 同格式（唯讀顯示 / clinic 面防呆用）。
 * 非編碼值（舊 option id / 空）回空字串。
 * @param {*} value
 * @returns {string}
 */
export function formatForDisplay (value) {
  if (!isEncodedFormQuantity(value)) return ''
  const q = parseToQuantities(value)
  return FORM_LEVELS.filter((lv) => q[lv] !== '' && q[lv] >= 1)
    .map((lv) => `${lv} x${q[lv]}`)
    .join(', ')
}
